import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, MapPin, Loader2, ClipboardCheck, Lock } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';
import flexcoaLogo from '../../assets/images/flexcoa-logo-condensed-green.png';

/**
 * Caregiver dashboard (FECOA-1320, post-login).
 *
 * Shows the caregiver's open ObsRO survey queue (caregiver-owned survey
 * windows) and the participants they are actively linked to. Tapping a survey
 * launches the reused survey-taking surface at /caregiver/survey/:assignmentId.
 */
const CaregiverDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      caregiverService.myLinks(),
      // The task queue is best-effort: a failure here must not blank the
      // dashboard, so swallow its error and fall back to an empty queue.
      caregiverService.getMyAssignments().catch(() => ({ assignments: [] })),
    ])
      .then(([linksRes, queueRes]) => {
        if (cancelled) return;
        setData(linksRes);
        setAssignments(queueRes?.assignments || []);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          navigate('/caregiver-login');
          return;
        }
        toast.error('Failed to load your account.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const logout = () => {
    caregiverService.logout();
    navigate('/caregiver-login');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-8 flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500 dark:text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen pt-8 flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <p className="text-sm text-gray-500 dark:text-gray-400">Unable to load your account.</p>
      </div>
    );
  }

  const links = data.links || [];

  return (
    <div className="min-h-screen pt-8 bg-gray-50 dark:bg-slate-950">
      {/* Minimal caregiver-app header — no breadcrumbs, no sponsor name, no nav. */}
      <header className="bg-white dark:bg-slate-900/60 border-b border-gray-200 dark:border-slate-800">
        <div className="max-w-2xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={flexcoaLogo} alt="flexCOA" className="h-7 w-auto" />
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-6">
        {/* Welcome */}
        <p className="text-xs uppercase text-gray-500 dark:text-gray-500">Welcome,</p>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {data.first_name} {data.last_name}
        </h1>
        <p className="text-xs text-primary-600 dark:text-primary-400 font-mono mt-1">{data.caregiver_id}</p>

        {/* ObsRO task queue — open caregiver-owned survey windows. Each card
            launches the (reused) survey-taking surface for that assignment. */}
        <h2 className="text-sm uppercase text-gray-500 dark:text-gray-400 mt-8 mb-3">
          Surveys to complete ({assignments.length})
        </h2>

        {assignments.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-md p-6 text-center">
            <ClipboardCheck className="w-6 h-6 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-900 dark:text-gray-300 font-medium mb-1">No tasks right now</p>
            <p className="text-xs text-gray-500">
              You&rsquo;ll see surveys here when your study coordinator schedules one for you.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {assignments.map((a) => (
              <button
                key={a.id}
                type="button"
                disabled={a.is_locked}
                onClick={() => navigate(`/caregiver/survey/${a.id}`)}
                className={
                  a.is_locked
                    ? 'w-full text-left bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-800 rounded-md p-4 opacity-60 cursor-not-allowed'
                    : 'w-full text-left bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-md p-4 hover:border-primary-400 dark:hover:border-primary-500 transition-colors'
                }
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{a.survey_name}</p>
                  {a.is_locked ? (
                    <Lock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <ClipboardCheck className="w-4 h-4 text-primary-500" />
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {a.is_locked
                    ? `Complete "${a.locked_by_survey_name}" first`
                    : `For participant ${a.participant?.participant_id}`}
                </p>
              </button>
            ))}
          </div>
        )}

        <h2 className="text-sm uppercase text-gray-500 dark:text-gray-400 mt-8 mb-3">
          Participants you support ({links.length})
        </h2>

        {links.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-md p-6 text-center">
            <User className="w-6 h-6 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-900 dark:text-gray-300 font-medium mb-1">No active participants</p>
            <p className="text-xs text-gray-500">
              You currently have no active study tasks. Your study coordinator will let you know
              when there&rsquo;s something to complete.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => (
              <div
                key={link.link_id}
                className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-md p-4"
              >
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Participant {link.participant.participant_id}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Relationship: {link.relationship_type}
                </p>
                <p className="text-xs text-gray-500 flex items-center mt-1">
                  <MapPin className="w-3 h-3 mr-1" />
                  {link.site.site_name} ({link.site.site_code})
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaregiverDashboard;
